import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect } from 'react';
import {
  answerChatQuestion,
  USER_CONVERSATION_ID,
} from '../../../api/conversations.api';
import { ChatChoiceChevronIcon } from '../../../assets/svg';
import { Button } from '../../../components/button/button';
import {
  ChatQuickReplies,
  ChatQuickReplyKey,
  ChatQuickReplyStatus,
} from '../../../types/supportTicket.types';
import { retireChatQuickReplies, upsertChatMessage } from '../chatCache';
import {
  isYesNoQuestion,
  QUICK_REPLY_STATUS_TEXT,
  quickReplyOffer,
  splitChoiceLabel,
} from '../helpers/chatTriage';
import { useChatCardAction } from '../hooks/useChatCardAction';
import {
  BubbleButtons,
  BubbleChoices,
  CardChip,
  CardPrompt,
  CardStatusRow,
  CardTone,
  ChoiceDetail,
  ChoiceRow,
  ChoiceText,
  ChoiceTitle,
} from './MessageCards.styled';

const STATUS_TONE: Record<ChatQuickReplyStatus, CardTone> = {
  open: 'wait',
  answered: 'good',
  replaced: 'neutral',
  closed: 'neutral',
};

type Props = {
  /** The bot's question. The answer is posted against its id. */
  messageId: number;
  quickReplies: ChatQuickReplies;
  /**
   * Merchant side: the buttons, drawn INSIDE the question's bubble under its
   * text. The admin gets a status line under the bubble instead - their copy
   * of the bot's message is red, and a status chip on red is nothing at all.
   * Where each goes is `MessageBubble`'s call.
   */
  interactive: boolean;
};

/**
 * Buttons on a question from the bot: Yes / No, or one per location when it
 * cannot tell which of the merchant's places a request is for.
 *
 * Not a card of its own: the question is an ordinary message and stays one.
 * The buttons live in its bubble the way Confirm and Cancel live in a card -
 * text above, buttons under it, time last - and simply go away once the
 * question stops waiting: answered, asked again further down, or closed.
 *
 * Tapping one is the one card-like action whose response says something. It
 * returns the "Yes" (or the location's label) the backend wrote into the
 * thread for the merchant, so that goes straight into the cache, and the
 * buttons come off on the spot rather than when the socket confirms it.
 */
export const QuickReplies = ({
  messageId,
  quickReplies,
  interactive,
}: Props) => {
  const queryClient = useQueryClient();
  const { busy, run, settle } = useChatCardAction();
  const { active, options, status } = quickReplies;

  /* Gone quiet by whatever route: our own tap, a typed reply, the bot asking
     again, or the merchant's other device. */
  useEffect(() => {
    if (!active) settle();
  }, [active, settle]);

  if (!interactive) {
    return (
      <CardStatusRow>
        <CardPrompt>{quickReplyOffer(options)}</CardPrompt>
        <CardChip $tone={STATUS_TONE[status] ?? 'neutral'}>
          {QUICK_REPLY_STATUS_TEXT[status] ?? QUICK_REPLY_STATUS_TEXT.closed}
        </CardChip>
      </CardStatusRow>
    );
  }

  if (!active || !options.length) return null;

  const answer = async (key: ChatQuickReplyKey) => {
    try {
      const { data } = await answerChatQuestion(messageId, key);
      upsertChatMessage(queryClient, USER_CONVERSATION_ID, data.data);
      retireChatQuickReplies(
        queryClient,
        USER_CONVERSATION_ID,
        messageId,
        'answered'
      );
    } catch (error) {
      /* Answered already (a double tap, the other device), asked again or
         closed: whichever it was, these buttons are spent. Off now, and the
         rethrow lets `run` refetch the thread to learn which. */
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        retireChatQuickReplies(queryClient, USER_CONVERSATION_ID, messageId);
      }
      throw error;
    }
  };

  if (!isYesNoQuestion(options)) {
    return (
      /* Held rather than tapped, the bubble would open its action sheet under
         the finger and swallow the tap - see the same guard on the cards. */
      <BubbleChoices onPointerDown={(event) => event.stopPropagation()}>
        {options.map((option) => {
          const { name, detail } = splitChoiceLabel(option.label);
          return (
            <ChoiceRow
              key={option.key}
              type="button"
              $picked={busy === option.key}
              disabled={!!busy}
              onClick={() => run(option.key, () => answer(option.key))}
            >
              <ChoiceText>
                <ChoiceTitle>{name}</ChoiceTitle>
                {detail && <ChoiceDetail>{detail}</ChoiceDetail>}
              </ChoiceText>
              <ChatChoiceChevronIcon />
            </ChoiceRow>
          );
        })}
      </BubbleChoices>
    );
  }

  return (
    <BubbleButtons onPointerDown={(event) => event.stopPropagation()}>
      {options.map((option) => (
        <Button
          key={option.key}
          $selected
          $gray={option.key !== 'yes'}
          $small
          $loading={busy === option.key}
          data-loading={busy === option.key ? 'true' : undefined}
          disabled={!!busy}
          onClick={() => run(option.key, () => answer(option.key))}
        >
          {option.label}
        </Button>
      ))}
    </BubbleButtons>
  );
};
